import React, { CSSProperties, useEffect, useState } from 'react';

interface ServiceMarkProps {
  code: string;
  name?: string;
}

interface CountryMarkProps {
  name: string;
}

export type SmsIconName =
  | 'activity'
  | 'antenna'
  | 'check'
  | 'chevronLeft'
  | 'chevronRight'
  | 'calendar'
  | 'cloud'
  | 'copy'
  | 'database'
  | 'globe'
  | 'history'
  | 'headphones'
  | 'home'
  | 'message'
  | 'phone'
  | 'plus'
  | 'refresh'
  | 'search'
  | 'smartphone'
  | 'wallet';

interface SmsIconProps {
  name: SmsIconName;
  className?: string;
}

const ICON_PATHS: Record<SmsIconName, React.ReactNode> = {
  activity: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  antenna: (
    <>
      <path d="M4.9 19.1a10 10 0 0 1 0-14.2M7.8 16.2a6 6 0 0 1 0-8.4" />
      <circle cx="12" cy="12" r="2" />
      <path d="m12 14 3 8m-3-8-3 8M19.1 4.9a10 10 0 0 1 0 14.2M16.2 7.8a6 6 0 0 1 0 8.4" />
    </>
  ),
  check: <path d="m5 12 4 4L19 6" />,
  chevronLeft: <path d="m15 18-6-6 6-6" />,
  chevronRight: <path d="m9 18 6-6-6-6" />,
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
    </>
  ),
  cloud: <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />,
  copy: <><rect x="8" y="8" width="12" height="13" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></>,
  database: (
    <>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5M12 7v5l3 2" />
    </>
  ),
  headphones: (
    <>
      <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
      <path d="M18 19h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v4a3 3 0 0 1-3 3ZM6 19H5a3 3 0 0 1-3-3v-4h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2Z" />
    </>
  ),
  home: (
    <>
      <path d="m3 11 9-8 9 8" />
      <path d="M5 10v10h14V10M9 20v-6h6v6" />
    </>
  ),
  message: <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />,
  phone: (
    <>
      <rect width="14" height="20" x="5" y="2" rx="2" />
      <path d="M9 5h6M12 18h.01" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  refresh: (
    <>
      <path d="M20 6v5h-5" />
      <path d="M4 18v-5h5M18.5 9A7 7 0 0 0 6.2 6.2L4 8M5.5 15A7 7 0 0 0 17.8 17.8L20 16" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </>
  ),
  smartphone: (
    <>
      <rect width="14" height="20" x="5" y="2" rx="2" />
      <path d="M12 18h.01" />
    </>
  ),
  wallet: (
    <>
      <path d="M20 7V5a2 2 0 0 0-2-2H5a3 3 0 0 0 0 6h15v10a2 2 0 0 1-2 2H5a3 3 0 0 1-3-3V6" />
      <path d="M16 13h2" />
    </>
  ),
};

export const SmsIcon: React.FC<SmsIconProps> = ({ name, className }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.8"
  >
    {ICON_PATHS[name]}
  </svg>
);

// Проверенные соответствия кодов Hero-SMS и готовых векторных знаков Simple Icons.
const SIMPLE_ICON_SLUGS: Record<string, string> = {
  go: 'google',
  ig: 'instagram',
  gp: 'ticketmaster',
  lf: 'tiktok',
  wa: 'whatsapp',
  fb: 'facebook',
  tg: 'telegram',
  ub: 'uber',
  ds: 'discord',
  tw: 'x',
  hw: 'alipay',
  ka: 'shopee',
  wb: 'wechat',
  wx: 'apple',
  oi: 'tinder',
  jg: 'grab',
  ni: 'gojek',
  vi: 'viber',
  fu: 'snapchat',
  kc: 'vinted',
  kt: 'kakaotalk',
  ts: 'paypal',
  nv: 'naver',
  ew: 'nike',
  pz: 'lidl',
  bw: 'signal',
  abk: 'gmx',
  ac: 'doordash',
  nf: 'netflix',
  zh: 'zoho',
  aiw: 'roblox',
  dh: 'ebay',
  aop: 'kleinanzeigen',
  nz: 'foodpanda',
  zs: 'bilibili',
  uk: 'airbnb',
  vm: 'okcupid',
  abo: 'webdotde',
  yy: 'venmo',
  zk: 'deliveroo',
  bnl: 'reddit',
  fz: 'kfc',
  pd: 'ifood',
  alj: 'spotify',
  ev: 'picpay',
  bxg: 'bat',
  nc: 'payoneer',
  vy: 'meta',
  li: 'baidu',
  ma: 'maildotru',
  acm: 'razer',
  vd: 'betfair',
  acz: 'claude',
  cn: 'fiverr',
  aaa: 'nubank',
  yn: 'allegro',
  ij: 'revolut',
  ef: 'nextdoor',
  mj: 'zalo',
  mt: 'steam',
  bkd: 'sahibinden',
  bo: 'wise',
  re: 'coinbase',
  ahe: 'bunq',
  cw: 'paddypower',
  aq: 'glovo',
  ry: 'mcdonalds',
  ahb: 'ubisoft',
  qv: 'badoo',
  amb: 'vercel',
  bxy: 'max',
  hx: 'aliexpress',
  ccu: 'googlechat',
  fk: 'blibli',
  hb: 'twitch',
  gq: 'freelancer',
  apf: 'carrefour',
  baq: 'redbubble',
  ber: 'gumtree',
  aav: 'alchemy',
  afz: 'klarna',
  aiz: 'brevo',
  ane: 'supercell',
  aon: 'binance',
  arp: 'continente',
  bbi: 'aiqfome',
  aor: 'okx',
  ayz: 'kimi',
  brk: 'indeed',
  et: 'clubhouse',
  sr: 'starbucks',
  tu: 'lyft',
  yu: 'xiaomi',
  zd: 'zilch',
  zl: 'airtel',
  abb: 'cocacola',
  abq: 'upwork',
  aed: 'bookingdotcom',
  aex: 'neon',
  aim: 'firebase',
  ano: 'shopify',
  aom: 'monzo',
  aoo: 'pegasusairlines',
  aqj: 'bigbasket',
  asp: 'phonepe',
  atn: 'fawry',
  atp: 'vonage',
  bbg: 'square',
  bbl: 'autodesk',
  ble: 'nexon',
  bny: 'suno',
  box: 'thumbtack',
  bp: 'gofundme',
  bpz: 'justeat',
  bqp: 'zara',
  bwa: 'hostinger',
  dp: 'protonmail',
  dy: 'zomato',
  ge: 'paytm',
  ip: 'burgerking',
  jx: 'swiggy',
  qy: 'zhihu',
  xs: 'groupme',
  zo: 'kaggle',
  qr: 'mega',
  blm: 'epicgames',
  of: 'jet',
  bv: 'metro',
  st: 'auchan',
  azn: 'ufc',
};

const SIMPLE_ICON_FALLBACKS: Record<string, string> = {
  am: '/sms/service-icons/amazon.svg',
  tn: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/linkedin.svg',
};

const LOCAL_ICON_SLUGS = new Set([
  'amazon', 'apple', 'facebook', 'google', 'instagram',
  'microsoft', 'paypal', 'telegram', 'tiktok', 'whatsapp',
]);

const LOCAL_ICON_COLORS: Record<string, string> = {
  amazon: '#ff9900',
  apple: '#007aff',
  facebook: '#1877f2',
  google: '#4285f4',
  instagram: '#e4405f',
  microsoft: '#00a4ef',
  paypal: '#003087',
  telegram: '#229ed9',
  tiktok: '#fe2c55',
  whatsapp: '#25d366',
};

// Коды долгосрочной аренды задаёт отдельный поставщик, поэтому для них знак
// определяется по стабильному отображаемому имени сервиса.
const SIMPLE_ICON_NAME_SLUGS: Record<string, string> = {
  aliexpress: 'aliexpress',
  amazon: 'amazon',
  americanexpress: 'americanexpress',
  apple: 'apple',
  discord: 'discord',
  facebook: 'facebook',
  google: 'google',
  instagram: 'instagram',
  microsoft: 'microsoft',
  paypal: 'paypal',
  signal: 'signal',
  snapchat: 'snapchat',
  taobao: 'taobao',
  telegram: 'telegram',
  tiktok: 'tiktok',
  uber: 'uber',
  viber: 'viber',
  wechat: 'wechat',
  whatsapp: 'whatsapp',
  x: 'x',
};

function normalizeServiceIdentity(value: string): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

export function resolveServiceIconUrl(code: string, name = ''): string | null {
  const normalized = String(code || '').trim().toLowerCase();
  if (SIMPLE_ICON_FALLBACKS[normalized]) return SIMPLE_ICON_FALLBACKS[normalized];
  const slug = SIMPLE_ICON_SLUGS[normalized]
    || SIMPLE_ICON_NAME_SLUGS[normalizeServiceIdentity(name)];
  if (!slug) return null;
  return LOCAL_ICON_SLUGS.has(slug)
    ? `/sms/service-icons/${slug}.svg`
    : `https://cdn.simpleicons.org/${slug}`;
}

function resolveLocalIconColor(code: string, name: string): string | null {
  const normalized = String(code || '').trim().toLowerCase();
  const slug = SIMPLE_ICON_SLUGS[normalized]
    || SIMPLE_ICON_NAME_SLUGS[normalizeServiceIdentity(name)];
  return slug ? LOCAL_ICON_COLORS[slug] || null : null;
}

export function getServiceMonogram(name: string, code: string): string {
  const source = String(name || code || '?').trim();
  const words = source.match(/[\p{L}\p{N}]+/gu) || [];
  if (words.length > 1) {
    return `${words[0]?.charAt(0) || ''}${words[1]?.charAt(0) || ''}`.toLocaleUpperCase('ru-RU');
  }
  return (words[0] || '?').slice(0, 2).toLocaleUpperCase('ru-RU');
}

function getServiceTone(code: string): number {
  const hash = Array.from(String(code || '')).reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return (hash % 7) + 1;
}

function normalizeCountryName(value: string): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-zа-яё0-9]+/giu, ' ')
    .trim();
}

const COUNTRY_ALIASES: Record<string, string> = {
  'dr congo': 'cd',
  'dem congo': 'cd',
  'democratic republic of the congo': 'cd',
  'ivory coast': 'ci',
  'south korea': 'kr',
  'north korea': 'kp',
  'russian federation': 'ru',
  russia: 'ru',
  usa: 'us',
  'united states': 'us',
  'united states of america': 'us',
  'сша': 'us',
  macao: 'mo',
  macau: 'mo',
  vietnam: 'vn',
  'czech republic': 'cz',
  palestine: 'ps',
};

let countryCodesByName: Map<string, string> | null = null;

function buildCountryCodeIndex(): Map<string, string> {
  const index = new Map<string, string>(Object.entries(COUNTRY_ALIASES));
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const displays = [
    new Intl.DisplayNames(['en'], { type: 'region' }),
    new Intl.DisplayNames(['ru'], { type: 'region' }),
  ];

  for (const first of alphabet) {
    for (const second of alphabet) {
      const code = `${first}${second}`;
      for (const display of displays) {
        const label = display.of(code);
        if (label && label !== code && !/unknown region|неизвестный регион/i.test(label)) {
          index.set(normalizeCountryName(label), code.toLowerCase());
        }
      }
    }
  }

  return index;
}

export function resolveCountryCode(name: string): string | null {
  if (!countryCodesByName) countryCodesByName = buildCountryCodeIndex();
  return countryCodesByName.get(normalizeCountryName(name)) || null;
}

export const ServiceMark: React.FC<ServiceMarkProps> = ({ code, name = '' }) => {
  const iconUrl = resolveServiceIconUrl(code, name);
  const brandColor = resolveLocalIconColor(code, name);
  const [imageFailed, setImageFailed] = useState(false);
  const hasLogo = Boolean(iconUrl && !imageFailed);

  useEffect(() => {
    setImageFailed(false);
  }, [iconUrl]);

  return (
    <span
      aria-hidden="true"
      className={`sms-entityMark is-service ${hasLogo ? `has-logo ${brandColor ? 'has-brand-color' : ''}` : `is-monogram tone-${getServiceTone(code)}`}`}
      style={brandColor ? ({ '--sms-service-brand': brandColor } as CSSProperties) : undefined}
      title={name || code}
    >
      {hasLogo ? (
        <img alt="" decoding="async" loading="lazy" src={iconUrl || undefined} onError={() => setImageFailed(true)} />
      ) : (
        <span className="sms-serviceMonogram">{getServiceMonogram(name, code)}</span>
      )}
    </span>
  );
};

export const CountryMark: React.FC<CountryMarkProps> = ({ name }) => {
  const countryCode = resolveCountryCode(name);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [countryCode]);

  return (
    <span aria-hidden="true" className="sms-entityMark is-country">
      {countryCode && !imageFailed ? (
        <img alt="" decoding="async" loading="lazy" src={`https://flagcdn.com/${countryCode}.svg`} onError={() => setImageFailed(true)} />
      ) : (
        <SmsIcon name="globe" />
      )}
    </span>
  );
};
